import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/** The two lenses on the evidence — the classic search browser and the
 *  network view — presented as one instrument: a segmented mode toggle at
 *  the top of both pages. Each side is a real route (shareable,
 *  back-button friendly); the `to` targets carry the current collection
 *  and term across so the flip preserves context. */

const styles = `
.evd-mode-toggle { display: flex; justify-content: center; }
/* The network lens is desktop-only; with one mode left, no toggle. */
@media (max-width: 768px) { .evd-mode-toggle { display: none; } }
.evd-mode-toggle .inner { display: inline-flex; border: 1px solid #dee2e6;
  border-radius: 4px; overflow: hidden; }
.evd-mode-toggle a { padding: 10px 26px; font-size: 15px; color: #212529;
  background: #fff; text-decoration: none; }
.evd-mode-toggle a.active { background: #212529; color: #fff; }
.evd-mode-toggle a:hover:not(.active) { background: #f4f2ef; color: #212529; }
`;

interface EvidenceModeToggleProps {
  mode: "search" | "graph";
  /** Route targets (standalone pages). Ignored when onSelect is given. */
  toSearch?: string;
  toGraph?: string;
  /** Embedded variant: switch a local mode instead of navigating. */
  onSelect?: (mode: "search" | "graph") => void;
}

const EvidenceModeToggle = ({
  mode, toSearch = "/evidence", toGraph = "/evidence/graph", onSelect,
}: EvidenceModeToggleProps) => {
  const { t } = useTranslation();
  const entries: { key: "search" | "graph"; to: string; label: string }[] = [
    { key: "graph", to: toGraph, label: `⌾ ${t("evidence.modeNetwork", "Network")}` },
    { key: "search", to: toSearch, label: `⌕ ${t("evidence.modeSearch", "Search")}` },
  ];
  return (
    <div className="evd-mode-toggle mb--30">
      <style>{styles}</style>
      <div className="inner" role="tablist">
        {entries.map(({ key, to, label }) => (onSelect ? (
          <a
            key={key}
            href="#evidence-explorer"
            role="tab"
            aria-selected={mode === key}
            className={mode === key ? "active" : ""}
            onClick={(event) => {
              event.preventDefault();
              onSelect(key);
            }}
          >
            {label}
          </a>
        ) : (
          <Link
            key={key}
            to={to}
            role="tab"
            aria-selected={mode === key}
            className={mode === key ? "active" : ""}
          >
            {label}
          </Link>
        )))}
      </div>
    </div>
  );
};

export default EvidenceModeToggle;
